import React, {
  ReactNode, useMemo, useState, Dispatch, SetStateAction, useCallback, useEffect,
  useContext, useRef,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { filterPlaneDataByAirportName } from 'utils';
import MainAppContext from 'contexts/MainAppContext';
import { SOCKET_SERVER_URL } from 'configs/endpoints';
import {
  ADSBPositions, ServerToClientEvents, ClientToServerEvents, SocketData, MESSAGE_TYPE,
} from './models';
import parseMessage from './messageParser';
import { PlaneHistory, updatePlaneHistory } from './planeHistory';

const socketConfig = {
  autoConnect: false,
  reconnection: true,
  reconnectionDelay: 5000,
  reconnectionAttempts: Infinity,
};
const mainAirport = process.env.REACT_APP_MAIN_AIRPORT_NAME || '';

interface ADSBContextValues {
  currentPositions: ADSBPositions[],
  setCurrentPositions: Dispatch<SetStateAction<ADSBPositions[]>>,
  activePlanePositions: ADSBPositions[] | undefined,
  updateActivePlane: (newPlane: ADSBPositions | undefined) => void,
  isConnected: boolean,
  setIsConnected: Dispatch<SetStateAction<boolean>>,
  pastAdsbMissing: boolean,
  setPastAdsbMissing: Dispatch<SetStateAction<boolean>>,
  adsbConnectionError: boolean,
  socket: Socket<ServerToClientEvents, ClientToServerEvents> | undefined,
}

interface ADSBProviderProps {
  children: ReactNode,
}

const ADSBContext = React.createContext<ADSBContextValues>({
  currentPositions: [],
  setCurrentPositions: () => {},
  activePlanePositions: undefined,
  updateActivePlane: (newPlane : ADSBPositions | undefined) => {},
  isConnected: false,
  setIsConnected: () => {},
  adsbConnectionError: false,
  pastAdsbMissing: false,
  setPastAdsbMissing: () => {},
  socket: undefined,
});

function ADSBProvider({ children }: ADSBProviderProps) {
  const [socket, setSocket] = useState<Socket<ServerToClientEvents, ClientToServerEvents>>();

  const [isConnected, setIsConnected] = useState(false);
  const [adsbConnectionError, setAdsbConnectionError] = useState(false);
  const [pastAdsbMissing, setPastAdsbMissing] = useState(false);
  const [currentPositions, setCurrentPositions] = useState<ADSBPositions[]>([]);
  const [planesLatestPositions, setPlanesLatestPositions] = useState<PlaneHistory>({});
  // modeS -> when the plane was last reported: used to drop planes that left the area.
  const planesLastSeen = useRef<{ [modeS: string]: number }>({});
  const [activePlaneModeS, setActivePlaneModeS] = useState<string | undefined>(undefined);
  const [activePlanePositions, setActivePlanePositions] = useState<
    ADSBPositions[] | undefined>(undefined);

  const { adsbIsEnabled } = useContext(MainAppContext);

  const onMessageEvent = useCallback((data: SocketData) : void => {
    if (data.type === MESSAGE_TYPE.ADS_B) {
      const positions = parseMessage(data);
      if (mainAirport) {
        setCurrentPositions(filterPlaneDataByAirportName(positions));
      } else {
        setCurrentPositions(positions);
      }
      if (adsbConnectionError) {
        setAdsbConnectionError(false);
      }
    } else if (data.type === MESSAGE_TYPE.ADSB_CONNECTION_ERROR) {
      setAdsbConnectionError(true);
    }
  }, [adsbConnectionError, setCurrentPositions, setAdsbConnectionError]);

  const onMessageEventRef = useRef(onMessageEvent);
  useEffect(() => { onMessageEventRef.current = onMessageEvent; }, [onMessageEvent]);

  useEffect(() => {
    if (adsbIsEnabled && !isConnected) {
      setAdsbConnectionError(true);
    } else {
      setAdsbConnectionError(false);
    }
  }, [adsbIsEnabled, isConnected]);

  useEffect(() => {
    const onConnect = () => {
      setIsConnected(true);
    };

    const onDisconnect = () => {
      setIsConnected(false);
    };

    // Stable listener that always calls the latest handler: the socket must not depend on the
    // handler's identity (it changes with adsbConnectionError) or it is torn down and rebuilt
    // on every connection flap.
    const onMessage = (data: SocketData) => onMessageEventRef.current(data);

    const newSocket:
      Socket<ServerToClientEvents, ClientToServerEvents> = io(SOCKET_SERVER_URL, socketConfig);
    newSocket.on('connect', onConnect);
    newSocket.on('disconnect', onDisconnect);
    newSocket.on('message', onMessage);

    setSocket(newSocket);

    return () => {
      newSocket.off('connect', onConnect);
      newSocket.off('disconnect', onDisconnect);
      newSocket.off('message', onMessage);
      newSocket.disconnect();
    };
  }, []);

  const updateActivePlane = useCallback((newPlane: ADSBPositions | undefined) => {
    if (newPlane && planesLatestPositions[newPlane.modeS]) {
      setActivePlaneModeS(newPlane.modeS);
      setActivePlanePositions(planesLatestPositions[newPlane.modeS]);
    } else {
      setActivePlaneModeS(undefined);
      setActivePlanePositions(undefined);
    }
  }, [planesLatestPositions]);

  useEffect(() => {
    const now = Date.now();
    currentPositions.forEach((position) => { planesLastSeen.current[position.modeS] = now; });
    setPlanesLatestPositions((prev) => updatePlaneHistory(
      prev,
      currentPositions,
      planesLastSeen.current,
      now,
    ));
  }, [currentPositions]);

  // The selected plane's trail follows its history (a sliding window) instead of being a snapshot
  // that drawPlanePath had to extend by mutating it.
  useEffect(() => {
    if (activePlaneModeS) {
      setActivePlanePositions(planesLatestPositions[activePlaneModeS]);
    }
  }, [planesLatestPositions, activePlaneModeS]);

  useEffect(() => {
    if (socket) {
      if (adsbIsEnabled) {
        socket.connect();
      } else {
        socket.disconnect();
      }
    }
  }, [adsbIsEnabled, socket]);

  const value = useMemo(() => ({
    currentPositions,
    setCurrentPositions,
    activePlanePositions,
    updateActivePlane,
    isConnected,
    setIsConnected,
    adsbConnectionError,
    pastAdsbMissing,
    setPastAdsbMissing,
    socket,
  }), [currentPositions, activePlanePositions, updateActivePlane,
    isConnected, adsbConnectionError, pastAdsbMissing, socket]);

  return (
    <ADSBContext.Provider value={value}>
      {children}
    </ADSBContext.Provider>
  );
}

export { ADSBProvider };
export default ADSBContext;
